import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Plus, Calendar, Clock, AlertTriangle, Check, Play, Settings, Search, Edit, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { CardGridSkeleton } from '@/components/ui/Skeletons';
import StatusTimeline from '@/components/ui/StatusTimeline';

const ProductionOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [processingRates, setProcessingRates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stageForm, setStageForm] = useState({ stage: '', quantity: '', location: '' });

  const [formData, setFormData] = useState({
    product_id: '',
    product_name: '',
    quantity: '',
    so_id: '',
    priority: 'normal',
    scheduled_start: '',
    location: '',
    assigned_to: '',
    notes: ''
  });

  const [editFormData, setEditFormData] = useState({
    quantity: '',
    priority: 'normal',
    location: '',
    assigned_to: '',
    notes: ''
  });

  const stages = ['printing', 'filling', 'sealing', 'packaging', 'ready_to_dispatch'];
  const priorities = ['low', 'normal', 'high', 'urgent'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes, soRes, usersRes, ratesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/production-orders`),
        axios.get(`${API}/finished-goods`),
        axios.get(`${API}/sales-orders`),
        axios.get(`${API}/users/list`),
        axios.get(`${API}/processing-rates`),
        axios.get(`${API}/warehouse-locations`)
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setSalesOrders(soRes.data.filter(so => so.status !== 'completed'));
      setUsers(usersRes.data);
      setProcessingRates(ratesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location) {
      toast.error('Location is required');
      return;
    }
    const product = products.find(p => p.id === formData.product_id);

    try {
      await axios.post(`${API}/production-orders`, {
        ...formData,
        product_name: product?.name || formData.product_name,
        quantity: parseFloat(formData.quantity)
      });
      toast.success('Production order created');
      setDialogOpen(false);
      setFormData({
        product_id: '',
        product_name: '',
        quantity: '',
        so_id: '',
        priority: 'normal',
        scheduled_start: '',
        location: '',
        assigned_to: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setEditFormData({
      quantity: order.quantity?.toString() || '',
      priority: order.priority || 'normal',
      location: order.location || '',
      assigned_to: order.assigned_to || '',
      notes: order.notes || '',
      scheduled_start: order.scheduled_start ? order.scheduled_start.split('T')[0] : '',
      scheduled_end: order.scheduled_end ? order.scheduled_end.split('T')[0] : ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.location) {
      toast.error('Location is required');
      return;
    }
    try {
      await axios.put(`${API}/production-orders/${editingOrder.id}`, {
        quantity: parseFloat(editFormData.quantity),
        priority: editFormData.priority,
        location: editFormData.location,
        assigned_to: editFormData.assigned_to,
        notes: editFormData.notes,
        scheduled_start: editFormData.scheduled_start || null,
        scheduled_end: editFormData.scheduled_end || null
      });
      toast.success('Production order updated');
      setEditDialogOpen(false);
      setEditingOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const openStageDialog = (order, specificStage = null) => {
    setSelectedOrder(order);
    const targetStage = specificStage || order.current_stage;
    const stageData = order.stage_quantities?.[targetStage] || { completed: 0, received: 0 };
    const pending = (stageData.received || order.quantity) - (stageData.completed || 0);
    const stageHistoryItem = [...(order.stage_history || [])].reverse().find(h => h.stage === targetStage);
    const defaultLocation = stageHistoryItem?.location || order.location || '';

    setStageForm({
      stage: targetStage,
      quantity: pending.toString(),
      currentCompleted: stageData.completed || 0,
      received: stageData.received || order.quantity,
      pending,
      location: defaultLocation
    });
    setStageDialogOpen(true);
  };

  const updateStage = async () => {
    const qty = parseFloat(stageForm.quantity) || 0;
    if (qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (!stageForm.location) {
      toast.error('Location is required');
      return;
    }

    const isComplete = (stageForm.currentCompleted + qty) >= selectedOrder.quantity;

    try {
      await axios.put(`${API}/production-orders/${selectedOrder.id}/stage`, {
        stage: stageForm.stage,
        completed: isComplete,
        quantity_completed: qty,
        location: stageForm.location || ''
      });
      toast.success(isComplete ? `${stageForm.stage} stage completed!` : `Added ${qty} units to ${stageForm.stage}`);
      setStageDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const saveProcessingRates = async () => {
    try {
      await axios.put(`${API}/processing-rates`, processingRates);
      toast.success('Processing rates saved');
      setRatesDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save rates');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.so_number && order.so_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700',
      normal: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[priority] || colors.normal;
  };

  const getStatusColor = (status) => {
    const colors = {
      planned: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.planned;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="h-10 w-72 bg-stone-200/60 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-40 bg-stone-200/60 rounded animate-pulse"></div>
            <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
        </div>
        <CardGridSkeleton cards={4} columns={1} />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="production-orders-page">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Production Orders</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Plan and track production independently or linked to sales orders</p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Dialog open={ratesDialogOpen} onOpenChange={setRatesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="configure-rates-button">
                  <Settings size={16} className="mr-2" />
                  Processing Rates
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Configure Processing Rates (Units per Day)</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-[hsl(145,20%,40%)]">
                    Set the expected processing rate for each stage. Working hours: 9:30 AM - 6:00 PM
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {processingRates.map((rate, idx) => (
                      <div key={rate.stage} className="p-3 border border-stone-200 rounded-sm">
                        <Label className="font-medium">{rate.stage.replace('_', ' ').toUpperCase()}</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            value={rate.units_per_day}
                            onChange={(e) => {
                              const updated = [...processingRates];
                              updated[idx].units_per_day = parseFloat(e.target.value) || 0;
                              setProcessingRates(updated);
                            }}
                          />
                          <span className="text-sm text-[hsl(145,20%,40%)]">units/day</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setRatesDialogOpen(false)}>Cancel</Button>
                    <Button onClick={saveProcessingRates} className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                      Save Rates
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="create-order-button">
                <Plus size={16} className="mr-2" />
                New Production Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Production Order</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between font-normal"
                        >
                          {formData.product_id
                            ? products.find(p => p.id === formData.product_id)?.name + ` (${products.find(p => p.id === formData.product_id)?.product_code})`
                            : "Select product"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search product..." />
                          <CommandList>
                            <CommandEmpty>No product found.</CommandEmpty>
                            <CommandGroup>
                              {products.map(p => (
                                <CommandItem
                                  key={p.id}
                                  value={p.name + " " + p.product_code}
                                  onSelect={() => {
                                    setFormData({ ...formData, product_id: p.id, product_name: p.name });
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${formData.product_id === p.id ? 'opacity-100' : 'opacity-0'}`} />
                                  {p.name} ({p.product_code})
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="Enter quantity"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Link to Sales Order (Optional)</Label>
                    <Select
                      value={formData.so_id}
                      onValueChange={(value) => setFormData({ ...formData, so_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No linked SO (Stock Production)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linked SO (Stock Production)</SelectItem>
                        {salesOrders.map(so => (
                          <SelectItem key={so.id} value={so.id}>{so.so_number} - {so.customer}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map(p => (
                          <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Scheduled Start</Label>
                    <Input
                      type="datetime-local"
                      value={formData.scheduled_start}
                      onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Assign To</Label>
                    <Select
                      value={formData.assigned_to}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to: value === 'unassigned' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Location <span className="text-red-500">*</span></Label>
                  <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(l => (
                        <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                    Create Order
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by PO number, product, or SO..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(order => {
          const completedStages = stages.filter(s => order.stages?.[s]).length;
          const progress = (completedStages / stages.length) * 100;

          return (
            <div key={order.id} className="bg-white rounded-sm border border-stone-200 shadow-sm p-6" data-testid={`order-${order.po_number}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-[hsl(145,60%,10%)]">{order.po_number}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                      {order.priority.toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {order.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-[hsl(145,60%,10%)] mt-1">{order.product_name}</p>
                  <p className="text-xs text-[hsl(145,20%,40%)]">
                    Quantity: {order.quantity} |
                    {order.so_number ? ` Linked to: ${order.so_number}` : ' Stock Production'}
                  </p>
                </div>
                <div className="text-right flex gap-2">
                  {order.status !== 'completed' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(order)}
                        data-testid={`edit-order-${order.po_number}`}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openStageDialog(order)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`update-stage-${order.po_number}`}
                      >
                        <Play size={14} className="mr-1" />
                        Update Progress
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Schedule Info */}
              {order.stage_schedule && Object.keys(order.stage_schedule).length > 0 && (
                <div className="mb-4 p-3 bg-stone-50 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={14} className="text-[hsl(145,60%,20%)]" />
                    <span className="text-xs font-medium text-[hsl(145,60%,10%)]">Production Schedule</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    {stages.map(stage => {
                      const schedule = order.stage_schedule[stage];
                      const isComplete = order.stages?.[stage];
                      const isCurrent = order.current_stage === stage;

                      const stageHistoryItem = [...(order.stage_history || [])].reverse().find(h => h.stage === stage);
                      const displayLocation = stageHistoryItem?.location || order.location;

                      return (
                        <div key={stage} className={`p-2 rounded ${isComplete ? 'bg-green-100' : isCurrent ? 'bg-blue-100' : 'bg-white'}`}>
                          <p className="font-medium">{stage.replace('_', ' ')}</p>
                          {displayLocation && <p className="text-xs text-[hsl(145,20%,40%)] truncate">Loc: {displayLocation}</p>}
                          {schedule && (
                            <>
                              <p className="text-[hsl(145,20%,40%)]">~{schedule.days_needed} days</p>
                              <p className="text-[hsl(145,20%,40%)]">End: {new Date(schedule.end).toLocaleDateString()}</p>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[hsl(145,20%,40%)]">Progress</span>
                  <span className="text-[hsl(145,60%,20%)] font-medium">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[hsl(145,60%,20%)] transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Stage Progress */}
              <div className="mt-6 mb-2">
                <StatusTimeline
                  stages={stages}
                  currentStage={order.current_stage}
                  completedStages={stages.filter(s => order.stages?.[s])}
                  stageDates={Object.fromEntries(
                    (order.stage_history || []).map(h => [h.stage, h.timestamp])
                  )}
                />

                {/* Additional detailed interaction points for stages */}
                <div className="flex mt-8 gap-2">
                  {stages.map((stage, idx) => {
                    const isCompleted = order.stages?.[stage];
                    const isCurrent = order.current_stage === stage;
                    const stageData = order.stage_quantities?.[stage] || { completed: 0, received: 0 };
                    const completed = typeof stageData === 'object' ? stageData.completed || 0 : 0;
                    const received = typeof stageData === 'object' ? stageData.received || 0 : order.quantity;
                    const pending = received - completed;

                    const hasWork = received > 0;
                    const canWork = hasWork && !isCompleted;

                    return (
                      <div key={`btn-${stage}`} className="flex-1 flex flex-col items-center">
                        <button
                          onClick={() => {
                            if (canWork || isCompleted) {
                              openStageDialog(order, stage);
                            }
                          }}
                          disabled={!hasWork && !isCompleted}
                          className={`text-[10px] w-full py-1.5 px-1 rounded-sm border transition-colors ${!hasWork && !isCompleted ? 'bg-stone-50 text-stone-300 border-stone-100 cursor-not-allowed' : 'cursor-pointer'} ${isCompleted
                            ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                            : isCurrent
                              ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 font-medium'
                              : hasWork
                                ? 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                                : 'bg-stone-50 text-stone-400 border-stone-100'
                            }`}
                        >
                          Update QTY
                        </button>
                        <div className="text-[10px] text-center mt-1">
                          <span className={`font-medium ${isCompleted ? 'text-green-600' : 'text-blue-600'}`}>{completed}</span>
                          <span className="text-stone-400">/{received}</span>
                          {pending > 0 && !isCompleted && (
                            <span className="block text-orange-500 font-medium text-[9px]">{pending} waiting</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-[hsl(145,20%,40%)]" data-testid="no-orders">
            No production orders found
          </div>
        )}
      </div>
      <div className="mt-4">
        <Pagination currentPage={currentPage} totalItems={filteredOrders.length} onPageChange={setCurrentPage} />
      </div>

      {/* Update Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Stage: {stageForm.stage?.replace('_', ' ').toUpperCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-sm">
              <p className="text-sm font-medium">{selectedOrder?.product_name}</p>
              <p className="text-xs text-[hsl(145,20%,40%)]">{selectedOrder?.po_number}</p>
              <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Already Done</p>
                  <p className="font-bold text-green-600">{stageForm.currentCompleted}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Total Received</p>
                  <p className="font-bold text-blue-600">{stageForm.received}</p>
                </div>
                <div>
                  <p className="text-xs text-[hsl(145,20%,40%)]">Pending</p>
                  <p className="font-bold text-orange-600">{stageForm.pending}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity Completed</Label>
                <Input
                  type="number"
                  value={stageForm.quantity}
                  onChange={(e) => setStageForm({ ...stageForm, quantity: e.target.value })}
                  max={stageForm.pending}
                />
              </div>
              <div>
                <Label>Location <span className="text-red-500">*</span></Label>
                <Select value={stageForm.location} onValueChange={(value) => setStageForm({ ...stageForm, location: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {parseFloat(stageForm.quantity) >= stageForm.pending && stageForm.pending > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                <p className="text-sm text-green-700 font-medium">
                  This will complete the {stageForm.stage?.replace('_', ' ')} stage!
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStageDialogOpen(false)}>Cancel</Button>
              <Button onClick={updateStage} className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                Update Progress
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Production Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Production Order {editingOrder?.po_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="p-3 bg-stone-50 rounded-sm mb-4">
              <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{editingOrder?.product_name}</p>
              <p className="text-xs text-[hsl(145,20%,40%)]">Current Stage: {editingOrder?.current_stage?.replace('_', ' ')}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={editFormData.quantity}
                  onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={editFormData.priority} onValueChange={(v) => setEditFormData({ ...editFormData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Start</Label>
                <Input
                  type="date"
                  value={editFormData.scheduled_start}
                  onChange={(e) => setEditFormData({ ...editFormData, scheduled_start: e.target.value })}
                />
              </div>
              <div>
                <Label>Scheduled End</Label>
                <Input
                  type="date"
                  value={editFormData.scheduled_end}
                  onChange={(e) => setEditFormData({ ...editFormData, scheduled_end: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location <span className="text-red-500">*</span></Label>
                <Select value={editFormData.location} onValueChange={(value) => setEditFormData({ ...editFormData, location: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Warehouse location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assign To</Label>
                <Select value={editFormData.assigned_to || 'unassigned'} onValueChange={(v) => setEditFormData({ ...editFormData, assigned_to: v === 'unassigned' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                Update Order
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionOrders;
